import React, { useState, useEffect } from "react";
import { apiPost, apiGet } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./ManagerBilling.css";

interface BillData {
  bill?: { net?: number; status?: string; balance?: number };
  patient?: { name?: string; patient_id?: number };
}

interface PaymentPanelProps {
  billId: number | string | null;
  onPaid?: () => void;
}

export default function PaymentPanel({ billId, onPaid }: PaymentPanelProps) {
  const [data, setData] = useState<BillData | null>(null);
  const [amount, setAmount] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [upiAmount, setUpiAmount] = useState("");
  const [mode, setMode] = useState("Cash");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { showToast } = useToast();

  const loadBill = async () => {
    if (!billId) return;
    setLoading(true);
    try {
      const value = await apiGet(`/api/manager/print/clinical/${billId}`);
      const res = value as BillData;
      setData(res);
      const pending = Number(res?.bill?.balance || 0);
      if (mode !== "Mix") {
        setAmount(pending > 0 ? String(pending) : "");
      }
      setCashAmount("");
      setUpiAmount("");
    } catch (err) {
      showToast({ message: "Failed to load bill details.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSuccess(false);
    loadBill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId]);

  const handlePayment = async () => {
    if (!billId) return;
    if (loading) return; // Prevent double submission

    const balance = Number(data?.bill?.balance ?? 0);

    // Validate
    let totalPaying = 0;
    if (mode === "Mix") {
      const c = Number(cashAmount || 0);
      const u = Number(upiAmount || 0);
      if (c <= 0 && u <= 0) {
        showToast({ message: "Enter at least one amount to split.", type: "warning" });
        return;
      }
      totalPaying = c + u;
      if (balance > 0 && totalPaying > balance + 0.01) {
        showToast({ message: `Payment (₹${totalPaying}) exceeds balance (₹${balance})`, type: "error" });
        return;
      }
    } else {
      if (!amount || Number(amount) <= 0) {
        showToast({ message: "Please enter a valid amount to pay.", type: "warning" });
        return;
      }
      totalPaying = Number(amount || 0);
      if (balance > 0 && totalPaying > balance + 0.01) {
        showToast({ message: `Payment (₹${totalPaying}) exceeds balance (₹${balance})`, type: "error" });
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === "Mix") {
        const c = Number(cashAmount || 0);
        const u = Number(upiAmount || 0);

        if (c > 0) {
          await apiPost("/api/manager/payment", { bill_id: billId, amount: c, method: "Cash" });
        }
        if (u > 0) {
          await apiPost("/api/manager/payment", { bill_id: billId, amount: u, method: "UPI" });
        }
      } else {
        await apiPost("/api/manager/payment", {
          bill_id: billId,
          amount: Number(amount),
          method: mode,
        });
      }

      setSuccess(true);
      setAmount("");
      setCashAmount("");
      setUpiAmount("");
      showToast({ message: "Payment recorded successfully!", type: "success" });

      await loadBill();
      if (onPaid) onPaid();
    } catch (err: unknown) {
      showToast({
        message: (err as Error)?.message || "Payment failed. Please try again.",
        type: "error",
      });
      setLoading(false); // Only unset loading on failure, keep it true on success to prevent double clicks during unmount
    }
  };

  if (!billId) return <div className="mod-text-muted" style={{ padding: '20px', textAlign: 'center' }}>No bill selected.</div>;
  if (loading && !data) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading bill details...</div>;

  const bill = data?.bill;
  const patient = data?.patient;

  // Modern input styles
  const inputStyle = {
      padding: '12px 16px',
      borderRadius: '8px',
      border: '1px solid #cbd5e1',
      fontSize: '1.2rem',
      fontWeight: 600,
      width: '100%',
      boxSizing: 'border-box' as const,
      outline: 'none',
      transition: 'border-color 0.2s',
      color: '#0f172a'
  };

  return (
    <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: 'var(--mod-shadow-sm)' }}>
      <div style={{ padding: '20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>💰 Receive Payment</h3>
        <span style={{ background: '#e0f2fe', color: '#0284c7', padding: '4px 10px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 700 }}>Bill #{billId}</span>
      </div>

      {bill && patient && (
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#fff' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Patient</div>
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem' }}>{patient.name}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Status</div>
            <div style={{ fontWeight: 800, color: bill.status === 'PAID' ? '#16a34a' : '#d97706' }}>{bill.status}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Total Bill</div>
            <div style={{ fontWeight: 700, color: '#334155', fontSize: '1rem' }}>₹{(bill.net || 0).toLocaleString()}</div>
          </div>
          <div style={{ background: '#fef2f2', padding: '8px', borderRadius: '8px', border: '1px dashed #fecaca' }}>
            <div style={{ fontSize: '0.75rem', color: '#991b1b', textTransform: 'uppercase', fontWeight: 800 }}>Balance Due</div>
            <div style={{ fontWeight: 800, color: '#dc2626', fontSize: '1.2rem' }}>₹{(bill.balance || 0).toLocaleString()}</div>
          </div>
        </div>
      )}

      {!success ? (
        <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '8px' }}>Select Payment Mode</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {["Cash", "UPI", "Card", "Mix"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{ 
                      flex: 1, padding: '10px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                      background: mode === m ? '#0ea5e9' : '#fff',
                      color: mode === m ? '#fff' : '#64748b',
                      border: mode === m ? '1px solid #0284c7' : '1px solid #cbd5e1',
                      boxShadow: mode === m ? '0 4px 6px -1px rgba(14, 165, 233, 0.3)' : 'none'
                  }}
                >
                  {m === "Mix" ? "Split Pay" : m}
                </button>
              ))}
            </div>
          </div>

          {mode === "Mix" ? (
            <div style={{ display: 'grid', gap: '16px', background: '#fff', padding: '16px', borderRadius: '12px', border: '1px dashed #cbd5e1', marginBottom: '20px' }}>
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>Cash Amount</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '1.2rem', fontWeight: 600 }}>₹</span>
                  <input type="number" placeholder="0" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} style={{ ...inputStyle, paddingLeft: '40px' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>UPI Amount</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '1.2rem', fontWeight: 600 }}>₹</span>
                  <input type="number" placeholder="0" value={upiAmount} onChange={(e) => setUpiAmount(e.target.value)} style={{ ...inputStyle, paddingLeft: '40px' }} />
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '1.1rem', color: '#334155' }}>
                Total Collecting: <strong style={{ color: '#16a34a', fontSize: '1.3rem' }}>₹{Number(cashAmount || 0) + Number(upiAmount || 0)}</strong>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '8px' }}>Amount to Collect</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '1.4rem', fontWeight: 700 }}>₹</span>
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                  style={{ ...inputStyle, paddingLeft: '44px', fontSize: '1.4rem', height: '60px' }}
                />
              </div>
            </div>
          )}

          <Button 
            variant="primary" 
            fullWidth 
            size="lg" 
            onClick={handlePayment} 
            isLoading={loading}
          >
            {mode === "Mix"
              ? `Confirm Split Payment (₹${Number(cashAmount || 0) + Number(upiAmount || 0)})`
              : `Confirm Payment (₹${amount || 0})`
            }
          </Button>
        </div>
      ) : (
        <div style={{ padding: '40px 20px', textAlign: 'center', background: '#f0fdf4', borderTop: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '1.4rem', color: '#166534' }}>Payment Successful!</h4>
          <p style={{ color: '#15803d', marginBottom: '24px' }}>The transaction has been recorded.</p>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button variant="outline" onClick={() => window.open(`/print?type=bill&bill=${billId}`, "_blank")}>
              🖨️ Print Receipt
            </Button>
            <Button variant="primary" onClick={() => { setSuccess(false); loadBill(); }}>
               Add Another Payment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
